import type { CustomizationHiddenMode } from "@tiendanube/nube-sdk-internal-types";

/**
 * The `hidden` option, as classes instead of inline styles.
 *
 * Every target that declares `hidden` reads and writes it through here, so
 * there is one stylesheet, one set of class names and one breakpoint on the
 * page no matter how many entries the catalog grows.
 *
 * **Why not `element.style.display`.** The Core themes write `display` on
 * these very elements themselves: selecting a variant runs the theme's own
 * `.show()` / `.hide()` over the product detail, which is an inline `display`
 * that would overwrite anything the SDK had put there. A class with
 * `!important` outranks that inline style, so a customization applied once
 * survives the shopper clicking through variants — and removing the class
 * hands the element straight back to the theme, which is what `reset` needs.
 *
 * @see ./handlers/README.md
 */

/** Marks the one `<style>` this module owns, so it is injected once. */
const STYLE_ELEMENT_ID = "nubesdk-customization-visibility";

/** Every mode except `"never"`, which is the absence of all of them. */
type HidingMode = Exclude<CustomizationHiddenMode, "never">;

/**
 * The class each mode applies. Exhaustive over `HidingMode`, so adding a mode
 * to the public union without giving it a class is a compile error.
 */
const HIDDEN_CLASS: Record<HidingMode, string> = {
	always: "js-nubesdk-hidden",
	mobile: "js-nubesdk-hidden-mobile",
	desktop: "js-nubesdk-hidden-desktop",
};

/** Derived from the record above, so it stays the single source of truth. */
const HIDING_MODES = Object.keys(HIDDEN_CLASS) as HidingMode[];
const EVERY_HIDDEN_CLASS = Object.values(HIDDEN_CLASS);

/**
 * A constant: no option value is ever interpolated into it. `768px` is
 * Bootstrap's `md`, the breakpoint the Core themes are built on, and the
 * mobile rule stops just below it so the two never both apply.
 *
 * `!important` is the point of the whole approach — see the note above.
 */
const STYLESHEET = `
.${HIDDEN_CLASS.always} { display: none !important; }
@media (max-width: 767.98px) { .${HIDDEN_CLASS.mobile} { display: none !important; } }
@media (min-width: 768px) { .${HIDDEN_CLASS.desktop} { display: none !important; } }
`;

/**
 * Put the stylesheet on the page, once, and only when something is actually
 * hidden: a page where no app hides anything gets no `<style>` at all.
 */
function ensureStylesheet(): void {
	const head = document.head;
	// Never throws: `operation` is not wrapped, so a missing `<head>` has to
	// mean "nothing applied", not a `command_failed` on the wire.
	if (!head || document.getElementById(STYLE_ELEMENT_ID)) return;

	const style = document.createElement("style");
	style.id = STYLE_ELEMENT_ID;
	style.textContent = STYLESHEET;
	head.appendChild(style);
}

/**
 * How `element` is hidden *by the SDK* right now.
 *
 * Deliberately blind to the theme's own `display`: the pristine snapshot has
 * to be something `writeHidden` can put back, and the theme's inline style is
 * not ours to restore. A virgin element therefore reads as `"never"`.
 */
export function readHidden(element: Element): CustomizationHiddenMode {
	for (const mode of HIDING_MODES) {
		if (element.classList.contains(HIDDEN_CLASS[mode])) return mode;
	}
	return "never";
}

/** Apply `mode` to `element`, replacing whatever mode it carried before. */
export function writeHidden(
	element: Element,
	mode: CustomizationHiddenMode,
): void {
	// Cleared first, so the modes can never accumulate on one element: an app
	// re-sending `set` on every variant change must land on the same state.
	element.classList.remove(...EVERY_HIDDEN_CLASS);
	if (mode === "never") return;

	const className = HIDDEN_CLASS[mode];
	// Defensive: `validate` has already refused anything that is not a mode,
	// and `classList.add(undefined)` would quietly add an "undefined" class.
	if (!className) return;

	ensureStylesheet();
	element.classList.add(className);
}
